import type { ReactNode } from 'react';
import { shortenFileName } from '../../../lib/format/shortenFileName';
import { Icon } from '../../icons/Icon';
import type { IconName } from '../../icons/registry';
import { Progress } from '../../shadcn/progress';
import styles from './AttachmentRow.module.css';

export const ATTACHMENT_ROW_TONES = ['default', 'danger'] as const;
export type AttachmentRowTone = (typeof ATTACHMENT_ROW_TONES)[number];

/** A determinate upload bar: `value` of `max`, named by `label` ("Uploading prices.pdf"). */
export interface AttachmentRowProgress {
    readonly value: number;
    readonly max: number;
    readonly label: string;
}

export interface AttachmentRowProps {
    /** The file name, shown shortened from the middle and read whole by a screen reader. Text, never markup. */
    readonly name: string;
    /** The file type's concept (`fileText`, `fileImage`, …; `attachment` for any other). */
    readonly icon: IconName;
    /** The line under the name: the size, then the state ("120 KB · Being checked"). */
    readonly meta: string;
    /** `danger` for a refused or failed file: the meta line in `tone/danger-text`, a `tone/danger-border` edge. */
    readonly tone?: AttachmentRowTone;
    /** Below the meta line: what else the row's state shows, such as a failed upload's `TraceLine`. */
    readonly detail?: ReactNode;
    /** A determinate bar below the meta line while the file uploads. */
    readonly progress?: AttachmentRowProgress | undefined;
    /** The row's actions — shadcn `Button`s the caller supplies (*Remove*, *Retry*, *Undo*). */
    readonly actions?: ReactNode;
    readonly 'data-testid'?: string;
    /** The caller's state name, for tests and the browser suite (`uploading`, `ready`, `removed`, …). */
    readonly 'data-state'?: string;
}

/**
 * Design-system §5 *Attachment row*, the compose variant (F20-h FR-C2; F20-h WI-1.2 plan `## UI / Design
 * Adherence`): a list item holding the type icon, the name shortened from the middle so its extension
 * stays visible (the full name in a visually hidden span, so a screen reader reads it whole), the meta
 * line, an optional determinate bar (shadcn `Progress`), and the caller's actions. It has no shadcn
 * counterpart; it renders inside the caller's `<ul>`.
 *
 * Every string renders as a React text child, so a file name holding markup shows as text (F20-h NFR-1).
 */
export function AttachmentRow({
    name,
    icon,
    meta,
    tone = 'default',
    progress,
    actions,
    detail,
    'data-testid': testId,
    'data-state': state,
}: AttachmentRowProps) {
    const shortName = shortenFileName(name);
    return (
        <li className={styles.row} data-state={state} data-testid={testId} data-tone={tone}>
            <span className={styles.glyph} data-icon={icon}>
                <Icon name={icon} size="sm" />
            </span>
            <div className={styles.text}>
                {/* Each name in a `<bdi>`, so a right-to-left override in it cannot reorder the extension. */}
                <p className={styles.name}>
                    <bdi aria-hidden="true">{shortName}</bdi>
                    <bdi className={styles.visuallyHidden}>{name}</bdi>
                </p>
                <p className={styles.meta}>{meta}</p>
                {detail}
                {progress === undefined ? null : (
                    <Progress
                        aria-label={progress.label}
                        className={styles.progress}
                        data-testid={testId === undefined ? undefined : `${testId}-progress`}
                        max={progress.max}
                        value={progress.value}
                    />
                )}
            </div>
            {actions === undefined || actions === null ? null : (
                <div className={styles.actions}>{actions}</div>
            )}
        </li>
    );
}
